import { generateScenarios } from '@/services/chatService'

const MUTATIONS = {
  SET_CURRENT_SUBJECT: 'SET_CURRENT_SUBJECT',
  SET_SCENARIOS: 'SET_SCENARIOS',
  SET_ACTIVE_SESSION: 'SET_ACTIVE_SESSION',
  CLEAR: 'CLEAR',
  // Audio mutations
  SET_CURRENT_TRANSCRIPTION: 'SET_CURRENT_TRANSCRIPTION',
  CLEAR_CURRENT_TRANSCRIPTION: 'CLEAR_CURRENT_TRANSCRIPTION',
  SET_AUDIO_RECORDING_STATE: 'SET_AUDIO_RECORDING_STATE',
  SET_TRANSCRIPTION_ERROR: 'SET_TRANSCRIPTION_ERROR',
  CLEAR_TRANSCRIPTION_ERROR: 'CLEAR_TRANSCRIPTION_ERROR',
  ADD_AUDIO_MESSAGE: 'ADD_AUDIO_MESSAGE',
  REMOVE_AUDIO_MESSAGE: 'REMOVE_AUDIO_MESSAGE',
  UPDATE_AUDIO_MESSAGE: 'UPDATE_AUDIO_MESSAGE',
  SET_TTS_ERROR: 'SET_TTS_ERROR',
  CLEAR_TTS_ERROR: 'CLEAR_TTS_ERROR'
}

const state = () => ({
  currentSubject: null, // { id: number, name: string }
  scenarios: [],        // Array of scenario objects returned by the backend
  activeSession: null,  // Current active session { id, subject_id, scenario_id, scenario_title, scenario_emoji, ... }
  sessions: [],         // Completed sessions

  // Audio-related state
  currentTranscription: null,  // { text: string, messageId: string, confidence: number }
  isRecording: false,          // Recording state
  transcriptionError: null,    // Error message if transcription failed
  audioMessages: {},           // Tracks pending audio synthesis { messageId: { status, url, error } }
  ttsError: null               // Text-to-speech error message
})

const mutations = {
  [MUTATIONS.SET_CURRENT_SUBJECT](state, subject) {
    state.currentSubject = subject
  },
  [MUTATIONS.SET_SCENARIOS](state, scenarios) {
    state.scenarios = scenarios
  },
  [MUTATIONS.SET_ACTIVE_SESSION](state, session) {
    state.activeSession = session
  },
  [MUTATIONS.CLEAR](state) {
    state.currentSubject = null
    state.scenarios = []
    state.activeSession = null
    state.currentTranscription = null
    state.isRecording = false
    state.transcriptionError = null
    state.audioMessages = {}
    state.ttsError = null
  },

  // Audio mutations
  [MUTATIONS.SET_CURRENT_TRANSCRIPTION](state, transcription) {
    state.currentTranscription = transcription
    state.transcriptionError = null
  },

  [MUTATIONS.CLEAR_CURRENT_TRANSCRIPTION](state) {
    state.currentTranscription = null
  },

  [MUTATIONS.SET_AUDIO_RECORDING_STATE](state, isRecording) {
    state.isRecording = isRecording
  },

  [MUTATIONS.SET_TRANSCRIPTION_ERROR](state, error) {
    state.transcriptionError = error
    state.currentTranscription = null
  },

  [MUTATIONS.CLEAR_TRANSCRIPTION_ERROR](state) {
    state.transcriptionError = null
  },

  [MUTATIONS.ADD_AUDIO_MESSAGE](state, { messageId, status = 'pending' }) {
    state.audioMessages[messageId] = {
      status,
      url: null,
      error: null,
      duration: 0
    }
  },

  [MUTATIONS.REMOVE_AUDIO_MESSAGE](state, messageId) {
    delete state.audioMessages[messageId]
  },

  [MUTATIONS.UPDATE_AUDIO_MESSAGE](state, { messageId, updates }) {
    if (state.audioMessages[messageId]) {
      Object.assign(state.audioMessages[messageId], updates)
    }
  },

  [MUTATIONS.SET_TTS_ERROR](state, error) {
    state.ttsError = error
  },

  [MUTATIONS.CLEAR_TTS_ERROR](state) {
    state.ttsError = null
  }
}

const actions = {
  setCurrentSubject({ commit }, subject) {
    commit(MUTATIONS.SET_CURRENT_SUBJECT, subject)
  },

  async fetchScenarios({ commit, state }, { language = 'ja', forceRegenerate = false } = {}) {
    const scenarios = await generateScenarios(state.currentSubject.id, language, forceRegenerate)
    commit(MUTATIONS.SET_SCENARIOS, scenarios)
  },

  setActiveSession({ commit }, session) {
    commit(MUTATIONS.SET_ACTIVE_SESSION, session)
  },

  clearChat({ commit }) {
    commit(MUTATIONS.CLEAR)
  },

  // Audio actions
  setCurrentTranscription({ commit }, transcription) {
    commit(MUTATIONS.SET_CURRENT_TRANSCRIPTION, transcription)
  },

  clearCurrentTranscription({ commit }) {
    commit(MUTATIONS.CLEAR_CURRENT_TRANSCRIPTION)
  },

  setAudioRecordingState({ commit }, isRecording) {
    commit(MUTATIONS.SET_AUDIO_RECORDING_STATE, isRecording)
  },

  setTranscriptionError({ commit }, error) {
    commit(MUTATIONS.SET_TRANSCRIPTION_ERROR, error)
  },

  clearTranscriptionError({ commit }) {
    commit(MUTATIONS.CLEAR_TRANSCRIPTION_ERROR)
  },

  addAudioMessage({ commit }, { messageId, status }) {
    commit(MUTATIONS.ADD_AUDIO_MESSAGE, { messageId, status })
  },

  removeAudioMessage({ commit }, messageId) {
    commit(MUTATIONS.REMOVE_AUDIO_MESSAGE, messageId)
  },

  updateAudioMessage({ commit }, { messageId, updates }) {
    commit(MUTATIONS.UPDATE_AUDIO_MESSAGE, { messageId, updates })
  },

  setTtsError({ commit }, error) {
    commit(MUTATIONS.SET_TTS_ERROR, error)
  },

  clearTtsError({ commit }) {
    commit(MUTATIONS.CLEAR_TTS_ERROR)
  }
}

const getters = {
  currentSubject: state => state.currentSubject,
  scenarios: state => state.scenarios,
  activeSession: state => state.activeSession,
  sessions: state => state.sessions,
  recentSessions: state =>
    [...state.sessions]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3),

  // Audio getters
  currentTranscription: state => state.currentTranscription,
  isRecording: state => state.isRecording,
  transcriptionError: state => state.transcriptionError,
  audioMessages: state => state.audioMessages,
  ttsError: state => state.ttsError,
  hasAudioError: state => !!(state.transcriptionError || state.ttsError),
  pendingAudioCount: state => Object.keys(state.audioMessages).filter(
    key => state.audioMessages[key].status === 'pending'
  ).length,
  getAudioMessage: state => (messageId) => state.audioMessages[messageId]
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
